import { useEffect, useRef } from 'react';
import { useTimeclockAdminStore } from '../store/useTimeclockAdminStore';
import { usePlanningStore } from '../store/usePlanningStore';
import { useEmployeeStore } from '../store/useEmployeeStore';
import { useAbsenceStore } from '../store/useAbsenceStore';
import { isoDate } from '../utils/dateUtils';
import { getMonthRange } from '../utils/listData';

interface Options {
  pollMs?: number;
}

/**
 * Loads everything the staff list needs for live + utilization signals and
 * keeps the daily attendance status fresh on a polling interval.
 */
export function useStaffListOps({ pollMs = 60_000 }: Options = {}) {
  const fetchDailyStatus = useTimeclockAdminStore((s) => s.fetchDailyStatus);
  const fetchAllEmployees = useEmployeeStore((s) => s.fetchAllEmployees);
  const fetchWeekEntries = usePlanningStore((s) => s.fetchWeekEntries);
  const fetchAbsences = useAbsenceStore((s) => s.fetchAbsences);

  const initialized = useRef(false);

  useEffect(() => {
    const today = new Date();
    const todayIso = isoDate(today);
    const month = getMonthRange(today);

    const pollLive = () => fetchDailyStatus(todayIso);

    const loadOnce = async () => {
      await Promise.allSettled([
        fetchAllEmployees(),
        // One range that covers the whole month — includes the running week.
        fetchWeekEntries(month.start, month.end),
        fetchAbsences({ startDate: month.start, endDate: month.end }),
      ]);
    };

    pollLive();
    if (!initialized.current) {
      loadOnce();
      initialized.current = true;
    }
    const id = setInterval(pollLive, pollMs);
    return () => clearInterval(id);
  }, [fetchDailyStatus, fetchAllEmployees, fetchWeekEntries, fetchAbsences, pollMs]);
}
